import React from "react";
import { Group } from "@scrile/api-provider/dist/api/GroupsProvider";
import { useTranslation } from "locales";
import { MediaFilesController } from "lib/MediaFileController";
import "./style.scss";

interface TypingUser {
  userId: string;
  userName: string;
}

interface Props {
  group: Group;
  threadId?: string;
  onBack?: () => void;
  onStartCall?: () => void;
  onStartVoiceCall?: () => void;
  activeCall?: boolean;
  onToggleSearch?: () => void;
  searchOpen?: boolean;
  typingUsers?: TypingUser[];
  onToggleMedia?: () => void;
  mediaOpen?: boolean;
  // Group Topics (Темы): when viewing a topic, show the topic name as the title
  // and the group name as the subtitle so the user knows which topic they're in.
  topicName?: string;
}

function ArrowLeft() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </svg>
  );
}

function PhoneCallIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
      <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" fill="currentColor" />
    </svg>
  );
}

function VideoCallIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="22" height="22">
      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path
        d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
        fill="currentColor"
      />
    </svg>
  );
}

function ChannelIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" className="group-chat-header__channel-icon">
      <path
        d="M3.4 20.4l2.3-7.1L2 9.5h7.2L12 2.1l2.8 7.4H22l-3.7 3.8 2.3 7.1-8.6-5.5-8.6 5.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function MediaIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" fill="currentColor" />
    </svg>
  );
}

function GroupChatHeader({ group, threadId, onBack, onStartCall, onStartVoiceCall, activeCall, onToggleSearch, searchOpen, typingUsers, onToggleMedia, mediaOpen, topicName }: Props) {
  const { t } = useTranslation();
  const initial = group.name ? group.name.charAt(0) : "G";
  const memberCount = group.memberIds?.length || 0;
  const isChannel = group.isChannel;
  const callsEnabled = !isChannel && group.callsEnabled;
  const hasTyping = typingUsers && typingUsers.length > 0;
  const avatarSrc = group.avatarUrl
    ? MediaFilesController.getImageThumbnail(group.avatarUrl, "240p")
    : null;

  return (
    <div className="group-chat-header">
      {onBack && (
        <div
          className="group-chat-header__back-button"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onBack(); }}
        >
          <ArrowLeft />
        </div>
      )}
      <div className="group-chat-header__avatar">
        {avatarSrc ? (
          <img src={avatarSrc} alt={group.name} className="group-chat-header__avatar-img" />
        ) : (
          initial
        )}
      </div>
      <div className="group-chat-header__content">
        <div className="group-chat-header__name">
          {isChannel && <ChannelIcon />}
          {topicName || group.name}
        </div>
        <div className="group-chat-header__members">
          {hasTyping ? (
            <span className="group-chat-header__typing">
              {typingUsers!.length === 1
                ? t("{{name}} is typing...", { name: typingUsers![0].userName })
                : t("{{count}} people typing...", { count: typingUsers!.length })}
            </span>
          ) : topicName ? (
            // In a topic: subtitle shows the parent group + member count.
            <>
              {group.name} · {memberCount} {isChannel ? t("subscribers") : t("members")}
              {activeCall && <span className="group-chat-header__call-active">{t("Call active")}</span>}
            </>
          ) : (
            <>
              {memberCount} {isChannel ? t("subscribers") : t("members")}
              {activeCall && <span className="group-chat-header__call-active">{t("Call active")}</span>}
            </>
          )}
        </div>
      </div>
      {onToggleSearch && (
        <button
          className={`group-chat-header__search-btn${searchOpen ? " __active" : ""}`}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onToggleSearch(); }}
          type="button"
          title={t("Search messages")}
        >
          <SearchIcon />
        </button>
      )}
      {callsEnabled && onStartVoiceCall && !activeCall && (
        <button
          className="group-chat-header__call-btn"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onStartVoiceCall(); }}
          type="button"
          title={t("Voice call")}
        >
          <PhoneCallIcon />
        </button>
      )}
      {callsEnabled && onStartCall && (
        <button
          className={`group-chat-header__call-btn${activeCall ? " __active" : ""}`}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onStartCall(); }}
          type="button"
          title={activeCall ? t("Join call") : t("Start call")}
        >
          <VideoCallIcon />
        </button>
      )}
      {threadId && onToggleMedia && (
        <button
          className={`group-chat-header__call-btn${mediaOpen ? " __active" : ""}`}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onToggleMedia(); }}
          type="button"
          title={t("Shared media")}
        >
          <MediaIcon />
        </button>
      )}
    </div>
  );
}

export default GroupChatHeader;
